<template>
    <div class="auth__signup_form">
        <div class="container">
            <div class="columns is-variable">
                <div class="column">
                    <div class="auth__signup_form_box">
                        <div class="auth__signup_form_box_title">{{ $t('auth.signUpForm.title.signUp') }}</div>
                        <template v-if="checkIsInvitation">
                            <div class="auth__signup_form_box_note">
                                <component-note>{{ $t('auth.signUpForm.title.willBeAccepted') }}</component-note>
                            </div>
                        </template>
                        <div class="auth__signup_form_box_fields">
                            <form-text
                                :disabled="checkIsInvitation"
                                :label="$t('auth.signUpForm.labels.email')"
                                :name="'signup-email'"
                                :v-validate="'required|email'"
                                :data-vv-as="'email'"
                                v-model="email"
                            />
                            <form-text
                                ref="newpasswordchallenge"
                                :password="true"
                                :label="$t('auth.login.labels.newPassword')"
                                :name="'login-new-password'"
                                :v-validate="{ required: true, regex: passwordRegex }"
                                :data-vv-as="'new password'"
                                @emit-enter="openTermsAndConditionsNotify"
                                v-model="password"
                            >Password length must be at least 8 and includes:<br>- lowercase letter<br>- uppercase letter<br>- special character<br>- number
                            </form-text>
                            <form-text
                                ref="confirmnewpasswordchallenge"
                                :password="true"
                                :label="$t('auth.login.labels.confirmPassword')"
                                :name="'login-confirm-new-password'"
                                :v-validate="{ required: true, regex: passwordRegex, is: password }"
                                :data-vv-as="'confirm new password'"
                                @emit-enter="openTermsAndConditionsNotify"
                                v-model="passwordConfirm"
                            />
                        </div>
                        <transition name="fade" mode="out-in">
                            <template v-if="error">
                                <div class="auth__signup_form_box_error">
                                    <component-note :danger="true">{{ error }}</component-note>
                                </div>
                            </template>
                        </transition>
                        <div class="auth__signup_form_box_actions">
                            <component-button
                                ref="signupsubmit"
                                :theme="'primary'"
                                :size="'full'"
                                :animate-loading="true"
                                :animate-loading-event-name="'user-signup'"
                                :name="'auth-signup-form-box-actions-signup'"
                                @click="openTermsAndConditionsNotify"
                            >{{ $t('auth.signUpForm.buttons.signUp') }}
                            </component-button>
                        </div>
                        <template v-if="!checkIsInvitation">
                            <component-separator class="auth__signup_form_box_separator"/>
                            <div class="auth__signup_form_box_tip">
                                <span>{{ $t('auth.signUpForm.title.doYouHave') }}</span>
                                <component-button
                                    :name="'auth-signup-form-box-actions-login'"
                                    :theme="'primary'"
                                    :text="true"
                                    :underline="true"
                                    :size="''"
                                    :to="{ name: 'auth-login' }"
                                >{{ $t('auth.signUpForm.buttons.logIn') }}
                                </component-button>
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </div>
        <pop-notify
            ref="termsandconditionsnotify"
            :title-text="'Terms and conditions'"
            :content-text="''"
            :size="'medium'"
            :cancel-button-text="'Back'"
            :submit-button-text="'Accept'"
            :close-button-active="false"
            :overlay-prevent-close="true"
            @emit-submit="handleSignUp"
        >
            <component-text
                :html="true"
                :is-slot="true"
            >
                <p>Welcome to Form LMS. <br>Use of our site constitutes acceptance of our <a href="https://www.melearning.co.uk/terms-and-conditions-lms" target="_blank">terms and conditions of use</a> and our <a href="https://www.melearning.co.uk/privacy-and-cookie-policy" target="_blank">privacy and cookie policy</a> and your agreement to be bound by them.</p>
            </component-text>
        </pop-notify>
    </div>
</template>

<script>
import FormText from '@/components/default/forms/FormText'
import ComponentButton from '@/components/default/shared/ComponentButton'
import ComponentSeparator from '@/components/default/shared/ComponentSeparator'
import ComponentNote from '@/components/default/shared/ComponentNote'
import { passwordRegex } from '@/variables'
import PopNotify from '@/components/default/shared/PopNotify'
import ComponentText from '@/components/default/shared/ComponentText'

export default {
  name: 'auth-signup-form',
  components: { ComponentText, PopNotify, ComponentNote, ComponentSeparator, ComponentButton, FormText },
  data () {
    return {
      passwordRegex
    }
  },
  computed: {
    email: {
      get () {
        return this.$store.getters.getAuthUserSignUpEmail
      },
      set (value) {
        this.$store.dispatch('authUserSetSignUpEmail', value)
      }
    },
    firstName: {
      get () {
        return this.$store.getters.getAuthUserSignUpFirstName
      },
      set (value) {
        this.$store.dispatch('authUserSetSignUpFirstName', value)
      }
    },
    lastName: {
      get () {
        return this.$store.getters.getAuthUserSignUpLastName
      },
      set (value) {
        this.$store.dispatch('authUserSetSignUpLastName', value)
      }
    },
    password: {
      get () {
        return this.$store.getters.getAuthUserSignUpPassword
      },
      set (value) {
        this.$store.dispatch('authUserSetSignUpPassword', value)
      }
    },
    passwordConfirm: {
      get () {
        return this.$store.getters.getAuthUserSignUpPasswordConfirm
      },
      set (value) {
        this.$store.dispatch('authUserSetSignUpPasswordConfirm', value)
      }
    },
    marketingAgree: {
      get () {
        return this.$store.getters.getAuthUserSignUpMarketingAgree
      },
      set (value) {
        this.$store.dispatch('authUserSetSignUpMarketingAgree', value)
      }
    },
    error () {
      return this.$store.getters.getAuthUserSignUpError
    },
    //
    checkIsInvitation () {
      return Boolean(this.$store.getters.getAuthUserSignUpInvitation)
    }
  },
  methods: {
    openTermsAndConditionsNotify () {
      this.$validator.resume()
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.$refs.termsandconditionsnotify.openNotify()
        }
      })
      this.$eventBus.$emit('stop-user-signup')
    },
    handleSignUp () {
      this.$refs.signupsubmit.startLoading()
      this.$store.dispatch('authUserSignUpVerified').finally(() => {
        this.$eventBus.$emit('stop-user-signup')
      })
      this.$validator.pause()
    }
  }
}
</script>

<style lang="scss">
    @import "~@/assets/scss/components/auth/signup/authsignupform";
</style>
